// src/components/Header.js
import React, { useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, CreditCard } from 'lucide-react';
import { MemberContext } from '../App';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { member } = useContext(MemberContext);

  const colors = {
    primary: '#2055df',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray600: '#475569',
    gray800: '#1e293b'
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/search', label: 'Search Drugs' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/top-prescriptions', label: 'Top 50' },
    { path: '/pharmacies', label: 'Pharmacies' },
    { path: '/blog', label: 'Blog' },
    { path: '/faq', label: 'FAQ' }
  ];

  const LogoComponent = () => {
    if (logoError) {
      // Fallback to text logo
      return (
        <>
          <div style={{
            width: 42, height: 42,
            background: `linear-gradient(135deg, ${colors.primary}, #4d7df7)`,
            borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 800, fontSize: 16
          }}>
            Rx
          </div>
          <span style={{ color: colors.gray800, fontSize: 22, fontWeight: 700 }}>ChatWithDr</span>
        </>
      );
    }

    return (
      <img
        src="/logo.png"
        alt="Rx.ChatWithDr.com"
        style={{
          height: 60,
          width: 'auto',
          maxWidth: 220,
          objectFit: 'contain'
        }}
        onError={() => setLogoError(true)}
      />
    );
  };

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'white', borderBottom: `1px solid ${colors.gray200}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 24px'
      }}>
        {/* Logo */}
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          textDecoration: 'none', fontWeight: 700, fontSize: 20
        }}>
          <LogoComponent />
        </Link>

        {/* Desktop Nav */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                color: location.pathname === link.path ? colors.primary : colors.gray600,
                background: location.pathname === link.path ? `${colors.primary}10` : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/card')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 20px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
            className="desktop-cta"
          >
            <CreditCard size={16} />
            {member ? 'My Card' : 'Get Card'}
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              padding: 8,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: colors.gray600
            }}
            className="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${colors.gray100}`,
          background: 'white'
        }} className="mobile-menu">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                display: 'block',
                padding: '12px 16px',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 500,
                textDecoration: 'none',
                color: location.pathname === link.path ? colors.primary : colors.gray600,
                background: location.pathname === link.path ? `${colors.primary}10` : 'transparent'
              }}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={() => { navigate('/card'); setMobileMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: 12
            }}
          >
            <CreditCard size={18} />
            {member ? 'My Card' : 'Get Free Card'}
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .desktop-cta { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </header>
  );
};

export default Header;